import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { applyMeta } from "../lib/meta";
import { readableOn } from "../lib/colors";
import { CategoryTag } from "../lib/categories";
import { Loader2, CalendarDays, MapPin, Ticket, Search, Repeat, X } from "lucide-react";
import { enablePwa } from "../pwa";
import InstallAppButton from "../components/InstallAppButton";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

export default function Storefront() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ticketCode, setTicketCode] = useState("");
  const [ticket, setTicket] = useState(null);
  const [tErr, setTErr] = useState("");
  const [month, setMonth] = useState("all");
  const [venueF, setVenueF] = useState("all");
  const [cityF, setCityF] = useState("all");
  const [artistF, setArtistF] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const checkTicket = async () => {
    setTErr(""); setTicket(null);
    if (!ticketCode.trim()) return;
    try {
      const { data } = await api.get(`/store/${slug}/voucher/${ticketCode.trim()}`);
      setTicket(data);
    } catch { setTErr("No ticket found with that code for this organiser."); }
  };

  useEffect(() => {
    api.get(`/store/${slug}`)
      .then(({ data }) => {
        setStore(data);
        const b = data.business;
        applyMeta({ title: b.seo_title || `${b.name} — Events & Tickets`, description: b.seo_description || b.description, image: b.share_image || b.hero_image || b.logo_url });
        if (b.pwa_enabled && b.pwa_active) {
          enablePwa({
            manifestUrl: `/api/store/${slug}/manifest.json?mode=store&origin=${encodeURIComponent(window.location.origin)}`,
            themeColor: b.pwa_theme_color || b.brand_color,
          });
        }
      })
      .catch(() => setError("Organiser not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-[#5a6b80]">{error}</div>;

  const b = store.business;
  const brand = b.brand_color || "#16243b";
  const btnColor = b.button_color || "";
  const textColor = b.text_color || "";
  const txtStyle = textColor ? { color: textColor } : undefined;
  const btnText = b.hub_button_text_color || (btnColor ? readableOn(btnColor) : "");
  const accentColor = b.hub_accent_color || "";
  const ctaCardCls = btnColor ? "rounded-full px-5 py-2 text-sm font-medium transition-opacity hover:opacity-90" : "gp-btn-teal rounded-full px-5 py-2 text-sm font-medium";
  const ctaStyle = btnColor || btnText ? { ...(btnColor ? { background: btnColor } : {}), ...(btnText ? { color: btnText } : {}) } : undefined;
  const gallery = (b.gallery_images || []).filter(Boolean);
  const mKey = (iso) => (iso ? String(iso).slice(0, 7) : "");
  const mLabel = (k) => { const [y, m] = k.split("-"); return new Date(+y, +m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }); };
  const all = store.products;
  const venueOpts = [...new Set(all.map((p) => p.venue_name).filter(Boolean))].sort();
  const cityOpts = [...new Set(all.map((p) => p.city).filter(Boolean))].sort();
  const monthOpts = [...new Set(all.map((p) => mKey(p.event_date)).filter(Boolean))].sort();
  const artistOpts = [...new Map(all.flatMap((p) => p.acts || []).map((a) => [a.slug, a])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const events = [...all]
    .filter((p) => (month === "all" || mKey(p.event_date) === month)
      && (venueF === "all" || (p.venue_name || "") === venueF)
      && (cityF === "all" || (p.city || "") === cityF)
      && (artistF === "all" || (p.acts || []).some((a) => a.slug === artistF)))
    .sort((a, c) => (a.event_date || "").localeCompare(c.event_date || ""));

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <div className="bg-[#0d1626] text-white" data-testid="store-topnav">
        <div className="max-w-[1100px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold tracking-tight hover:text-[#2ec9c9] transition-colors" data-testid="store-nav-brand">TicketsPro.app</Link>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/" className="text-white/80 hover:text-white transition-colors" data-testid="store-nav-home">Home</Link>
            <Link to="/events" className="text-white/80 hover:text-white transition-colors" data-testid="store-nav-all-events">All events</Link>
          </div>
        </div>
      </div>
      <div className="relative" style={{ background: brand }}>
        {b.hero_image && <img src={b.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative max-w-[1100px] mx-auto px-6 py-20 text-center">
          {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-32 sm:h-36 mx-auto mb-6 object-contain" data-testid="store-hero-logo" />}
          <h1 className="text-white text-[2.6rem] font-bold" style={txtStyle} data-testid="store-hero-name">{b.name}</h1>
          <p className="text-white/85 text-[1.15rem] mt-3 max-w-[600px] mx-auto" style={txtStyle}>{b.description}</p>
          {b.pwa_enabled && b.pwa_active && (
            <div className="mt-6">
              <InstallAppButton className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/40 text-white px-5 py-2.5 text-sm font-medium backdrop-blur hover:bg-white/25 transition-colors" label="Install app" />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h2 className="text-[1.8rem] font-bold text-[#16243b]" style={txtStyle}>Upcoming events</h2>
          <div className="flex flex-wrap gap-2">
            {monthOpts.length > 1 && (
              <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="store-filter-date"
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="all">Any date</option>
                {monthOpts.map((m) => <option key={m} value={m}>{mLabel(m)}</option>)}
              </select>
            )}
            {venueOpts.length > 1 && (
              <select value={venueF} onChange={(e) => setVenueF(e.target.value)} data-testid="store-filter-venue"
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="all">All venues</option>
                {venueOpts.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            {cityOpts.length > 1 && (
              <select value={cityF} onChange={(e) => setCityF(e.target.value)} data-testid="store-filter-city"
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="all">All cities</option>
                {cityOpts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {artistOpts.length > 0 && (
              <select value={artistF} onChange={(e) => setArtistF(e.target.value)} data-testid="store-filter-artist"
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="all">All artists</option>
                {artistOpts.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
              </select>
            )}
          </div>
        </div>
        {events.length === 0 ? (
          <p className="text-[#5a6b80]">No events on sale yet. Please check back soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((p) => {
              const prices = (p.ticket_tiers || [{ price: p.price }]).map((t) => t.price);
              const from = Math.min(...prices);
              const multiPrice = new Set(prices).size > 1;
              const soldPct = p.show_sold_bar && typeof p.sold_pct === "number" ? p.sold_pct : null;
              return (
                <Link key={p.id} to={`/store/${slug}/events/${p.slug}`} data-testid={`event-card-${p.slug}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(20,30,55,0.07)] hover:shadow-[0_18px_40px_rgba(20,30,55,0.13)] transition-all duration-300 hover:-translate-y-1 flex flex-col">
                  <div className="h-44 bg-gray-100 overflow-hidden relative">
                    {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center" style={{ background: brand }}><Ticket className="text-white/70" size={36} /></div>}
                    {p.event_date && <span className="absolute top-3 left-3 bg-white/95 text-[#16243b] text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(p.event_date)}</span>}
                    {!p.sold_out && p.low_stock && <span className="absolute top-3 right-3 bg-[#c0392b] text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid={`card-lowstock-${p.slug}`}>Only {p.tickets_left} left!</span>}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-semibold text-[#16243b] text-[1.1rem]" style={txtStyle}>{p.title}</h3>
                    {p.series_id && <span data-testid={`series-badge-${p.slug}`} className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#eefafa] text-[#1fb0b0]"><Repeat size={10} /> Recurring series</span>}
                    {p.category && <div className="mt-1.5"><CategoryTag category={p.category} /></div>}
                    {p.venue_name && <p className="text-[#5a6b80] text-sm mt-1 flex items-center gap-1" style={accentColor ? { color: accentColor } : undefined}><MapPin size={13} /> {p.venue_slug && p.city_slug ? <span role="link" tabIndex={0} data-testid={`card-venue-link-${p.slug}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/venues/${p.city_slug}/${p.venue_slug}`); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); navigate(`/venues/${p.city_slug}/${p.venue_slug}`); } }} className="hover:text-[#2ec9c9] hover:underline cursor-pointer">{p.venue_name}</span> : p.venue_name}</p>}
                    <p className="text-[#5a6b80] text-sm mt-1 flex-1 line-clamp-2">{p.description}</p>
                    {soldPct !== null && !p.tickets_disabled && (
                      <div className="mt-3" data-testid={`card-sold-bar-${p.slug}`}>
                        <div className="flex items-center justify-between text-[11px] font-semibold text-[#5a6b80] mb-1">
                          <span>{soldPct >= 100 ? "Sold out" : `${soldPct}% sold`}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(3, Math.min(100, soldPct))}%`, background: soldPct >= 90 ? "#c0392b" : "#2ec9c9" }} />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-[#16243b]" style={txtStyle}>{p.tickets_disabled ? <span className="text-[1.1rem] font-bold text-[#2ec9c9]" style={accentColor ? { color: accentColor } : undefined}>Free event</span> : from <= 0 ? <span className="text-[1.4rem] font-bold text-green-600">Free</span> : <>{multiPrice && <span className="text-xs text-[#8a97a8]" style={accentColor ? { color: accentColor, opacity: 0.8 } : undefined}>from </span>}<span className="text-[1.4rem] font-bold" style={accentColor ? { color: accentColor } : undefined} data-testid={`card-price-${p.slug}`}>£{from.toFixed(2)}</span></>}</span>
                      {p.sold_out ? (
                        <span className="rounded-full px-5 py-2 text-sm font-semibold bg-[#c0392b] text-white" data-testid={`card-soldout-${p.slug}`}>Sold out</span>
                      ) : (
                        <span className={ctaCardCls} style={ctaStyle}>{p.tickets_disabled ? "More info" : from <= 0 ? "Get free ticket" : "Get tickets"}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Check a ticket */}
      <div className="max-w-[1100px] mx-auto px-6 pb-16">
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(20,30,55,0.07)] p-6" data-testid="store-check-ticket">
          <h2 className="text-[1.4rem] font-bold text-[#16243b] mb-1" style={txtStyle}>Check a ticket</h2>
          <p className="text-[#5a6b80] text-sm mb-4">Enter a {b.name} ticket code to see its status.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={ticketCode} onChange={(e) => { setTicketCode(e.target.value.toUpperCase()); setTErr(""); setTicket(null); }}
              placeholder="e.g. ABCD123456" data-testid="store-ticket-input"
              className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono" />
            <button onClick={checkTicket} data-testid="store-ticket-btn" className={btnColor ? "rounded-xl px-6 py-2.5 font-medium flex items-center gap-2 transition-opacity hover:opacity-90" : "gp-btn-teal rounded-xl px-6 py-2.5 font-medium flex items-center gap-2"} style={ctaStyle}><Search size={16} /> Check ticket</button>
          </div>
          {tErr && <p className="text-red-500 text-sm mt-3" data-testid="store-ticket-error">{tErr}</p>}
          {ticket && (
            <div className="mt-4 bg-[#eefafa] rounded-xl p-4" data-testid="store-ticket-result">
              <p className="text-[#16243b] font-semibold">{ticket.product_title} — {ticket.tier_name}</p>
              <p className="text-[#5a6b80] text-sm">Attendee: {ticket.attendee_name || "—"}</p>
              <p className="text-sm mt-1 font-medium" style={{ color: ticket.checked_in ? "#d97706" : "#16a34a" }}>
                {ticket.checked_in ? "Already checked in" : "Valid — not yet used"}
              </p>
            </div>
          )}
        </div>
      </div>

      {gallery.length > 0 && (
        <div className="max-w-[1100px] mx-auto px-6 pb-16" data-testid="store-gallery">
          <h2 className="text-[1.4rem] font-bold text-[#16243b] mb-4" style={txtStyle}>Gallery</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.map((src, i) => (
              <button key={i} onClick={() => setLightbox(src)} data-testid={`gallery-img-${i}`}
                className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2ec9c9]">
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors"><X size={30} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] rounded-xl object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {(b.website || (b.social_links || []).length > 0) && (
        <div className="max-w-[1100px] mx-auto px-6 pb-10 flex flex-wrap items-center gap-4" data-testid="store-links">
          {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="text-[#1fb0b0] font-medium hover:underline">Visit our website →</a>}
          {(b.social_links || []).filter((s) => s.url).map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[#5a6b80] text-sm capitalize hover:text-[#16243b]">{s.platform}</a>
          ))}
        </div>
      )}

      <footer className="text-center py-8 text-[#8a97a8] text-sm">Powered by TicketsPro.app</footer>
    </div>
  );
}
